<template>
    <div>
        <FormHeader />
        <ShowStep :current="2"/>
        <SectionLabel>申込者情報</SectionLabel>
        <section class="content">
            <FormConfirm label="ココシンクID" :value="request.cocolink_id" v-if="request.cocolink_id"/>
            <FormConfirm label="お名前" :value="`${request.name1} ${request.name2}`"/>
            <FormConfirm label="フリガナ" :value="`${request.kana1} ${request.kana2}`"/>
            <FormConfirm label="郵便番号" :value="request.postcode"/>
            <FormConfirm label="都道府県" :value="request.pref"/>
            <FormConfirm label="市区町村" :value="request.city"/>
            <FormConfirm label="それ以降の住所" :value="request.address"/>
            <FormConfirm label="マンション・建物名など（任意）" :value="request.bill"/>
            <FormConfirm label="電話番号" :value="request.tel"/>
            <FormConfirm label="メールアドレス" :value="request.email"/>
            <FormConfirm label="職種" :value="request.industry"/>
            <FormConfirm label="お支払い方法" :value="paymentLabel"/>
            <div v-if="request.payment_type === 'credit'">
                <FormConfirm label="お支払い回数" :value="payTimes"/>
            </div>
            <div v-if="request.payment_type === 'stripe'">
                <FormConfirm label="お支払い回数" :value="payTimes"/>
            </div>
        </section>
        <SectionLabel>申し込み詳細</SectionLabel>
        <section class="content">
            <ApplyDetailTable :price="price" :is-selected="true"/>
            <div class="mt-8 flex flex-col-reverse items-center lg:flex-row lg:items-start lg:justify-center px-4">
                <div class="w-full flex justify-center lg:w-auto lg:mr-8">
                    <button type="button" class="cancel-button" @click="$emit('cancel')" :disabled="isProcessing">入力に戻る</button>
                </div>
                <!-- クレジットカード・後払いの場合のみ従来の送信ボタン表示 -->
                <div v-if="!isWalletPayment" class="w-full flex justify-center mb-4 lg:mb-0 lg:w-auto lg:ml-8">
                    <button type="button" class="submit-button" @click="$emit('submit')" :disabled="isProcessing">
                        申し込みをする
                    </button>
                </div>
                <!-- Apple Pay / Google Pay の場合: Express Checkout ボタン表示 -->
                <div v-if="isWalletPayment" class="wallet-checkout-section w-full flex flex-col items-center mb-4 lg:mb-0 lg:w-auto lg:mt-8 lg:ml-8">
                    <div id="express-checkout-element"
                        class="express-checkout-element"
                        ref="expressCheckoutElement">
                    </div>
                    <p v-if="walletError" style="color:#c00; margin-top:8px;">
                        {{ walletError }}
                    </p>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import SectionLabel from "./SectionLabel";
import FormLabel from "./FormLabel";
import FormConfirm from "./FormConfirm";
import ApplyDetailTable from "./ApplyDetailTable";
import ConfirmHeader from "./ConfirmHeader";
import FormHeader from "./FormHeader.vue";
import ShowStep from "../../parts/app/ShowStep";
import { loadStripe } from "@stripe/stripe-js";
import { mapActions } from "vuex";

export default {
    name: "StartConfirm",
    components: {ConfirmHeader, FormHeader, ApplyDetailTable, FormConfirm, FormLabel, SectionLabel, ShowStep},
    props: {
        request: {
            type: Object,
        },
        isProcessing: {
            type: Boolean
        },
        price: {
            type: Object
        },
        payment: {
            type: Object
        },
        clientSecret: {
            type: String,
            default: null
        },
        orderId: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            stripe: null,
            elements: null,
            expressCheckoutElement: null,
            walletError: '',
        }
    },

    computed: {
        isWalletPayment() {
            return this.request.payment_type === 'apple_pay'
                || this.request.payment_type === 'google_pay';
        },

        paymentLabel() {
            switch (this.request.payment_type) {
                case 'stripe': return 'クレジットカード';
                case 'credit': return 'クレジットカード';
                case 'apple_pay': return 'Apple Pay';
                case 'google_pay': return 'Google Pay';
                case 'later': return 'コンビニ後払い';
                default: return '';
            }
        },

        payTimes() {
            if (this.request.pay_times === 'bonus') {
                return 'ボーナス払';
            } else {
                return `${this.request.pay_times}回払`;
            }
        },

    },
    async mounted() {
        if (this.isWalletPayment) {
            await this.initializeExpressCheckout();
        }
    },

    methods: {
        ...mapActions('apply_confirm', [
            'execConfirmApply',
        ]),

        async initializeExpressCheckout() {
            try {
                // 1. Stripe 初期化
                this.stripe = await loadStripe(process.env.MIX_STRIPE_KEY);
                if (!this.stripe) {
                    this.walletError = '決済システムの初期化に失敗しました。';
                    return;
                }

                // 2. PaymentIntent を作成（サーバーサイド）
                if (!this.clientSecret || !this.orderId) {
                    this.walletError = '決済情報の取得に失敗しました。';
                    return;
                }
                const intentResult = { client_secret: this.clientSecret, order_id: this.orderId };

                // 3. Elements 作成（PaymentIntent の client_secret を使用）
                this.elements = this.stripe.elements({
                    clientSecret: intentResult.client_secret,
                    appearance: { theme: 'stripe' },
                });

                // 4. Express Checkout Element 作成
                // 両方 'always' にすることで、環境に応じて利用可能なウォレットボタンを自動表示する
                const isApplePay = this.request.payment_type === 'apple_pay';
                this.expressCheckoutElement = this.elements.create('expressCheckout', {
                    buttonHeight: 55,
                    paymentMethods: {
                        applePay: isApplePay ? 'always' : 'never',
                        googlePay: isApplePay ? 'never' : 'always',
                        link: 'never',
                    },
                    buttonType: {
                        applePay: 'buy',
                        googlePay: 'buy',
                    },
                    buttonTheme: {
                        applePay: 'black',
                        googlePay: 'black',
                    },
                });

                // 5. DOM にマウント
                await this.$nextTick();
                const mountPoint = this.$refs.expressCheckoutElement
                    || document.getElementById('express-checkout-element');
                if (mountPoint) {
                    this.expressCheckoutElement.mount(mountPoint);
                }

                // 6. confirm イベント: 外部決済サービスで「今すぐ支払う」が押された
                this.expressCheckoutElement.on('confirm', async (event) => {
                    await this.handleWalletPaymentConfirm(event, intentResult);
                });

                // 7. cancel イベント: ユーザーが決済シートをキャンセルした
                this.expressCheckoutElement.on('cancel', () => {
                    this.walletError = '';
                });

                // 8. ready イベント: ボタンの表示可否を判定
                this.expressCheckoutElement.on('ready', ({availablePaymentMethods}) => {
                    if (!availablePaymentMethods) {
                        this.walletError = 'お使いの環境では ' +
                            (this.request.payment_type === 'apple_pay' ? 'Apple Pay' : 'Google Pay') +
                            ' をご利用いただけません。「入力に戻る」ボタンから別のお支払い方法を選択してください。';
                    }
                });

            } catch (e) {
                console.error('Express Checkout initialization failed:', e);
                this.walletError = '決済ボタンの表示に失敗しました。';
            }
        },

        /**
         * 外部決済サービスでの決済完了後のコールバック
         */
        async handleWalletPaymentConfirm(event, intentResult) {
            this.$emit('update:isProcessing', true);
            // 実際に使用されたウォレットタイプを取得（apple_pay or google_pay）
            const actualPaymentType = event.expressPaymentType || 'apple_pay';
            try {
                // 1. Stripe confirmPayment 実行
                const { error, paymentIntent } = await this.stripe.confirmPayment({
                    elements: this.elements,
                    confirmParams: {
                        return_url: window.location.origin + '/thank_you' + `?m=${btoa(this.request.email)}&t=${btoa(this.request.tel)}`,
                        payment_method_data: {
                            billing_details: {
                                name: `${this.request.name1} ${this.request.name2}`,
                                email: this.request.email,
                                phone: this.request.tel,
                                address: {
                                    postal_code: this.request.postcode,
                                    state: this.request.pref,
                                    city: this.request.city,
                                    line1: this.request.address,
                                    line2: this.request.bill,
                                    country: 'JP'
                                }
                            }
                        }
                    },
                    redirect: 'if_required',
                });

                if (error) {
                    this.walletError = error.message || '決済に失敗しました。';
                    return;
                }

                // 2. 決済成功: 本登録 API 呼び出し
                if (paymentIntent && paymentIntent.status === 'succeeded') {
                    const deviceInfo = navigator.userAgent || 'unknown';
                    try {
                        const confirmResult = await this.execConfirmApply({
                            ...this.request,
                            payment_type: actualPaymentType, // 実際に使用されたウォレットタイプを送信
                            order_id: intentResult.order_id,
                            stripe_intent_id: paymentIntent.id,
                            device_info: deviceInfo,
                        });

                        if (confirmResult?.success) {
                            this.$router.replace({
                                path: '/thank_you',
                            });
                        } else {
                            this.walletError = '申込確定に失敗しました。';
                        }
                    } catch (confirmError) {
                        console.error('Confirm apply failed:', confirmError);
                        this.walletError = '申込確定処理中にエラーが発生しました。';
                    }
                } else {
                    this.walletError = '決済が完了しませんでした。再度お試しください。';
                }

            } catch (e) {
                console.error('Wallet payment failed:', e);
                this.walletError = '決済処理中にエラーが発生しました。';
            } finally {
                this.$emit('update:isProcessing', false);
            }
        },
    },

    beforeDestroy() {
        if (this.expressCheckoutElement) {
            try {
                this.expressCheckoutElement.unmount();
            } catch (e) {
                /* ignore */
            }
        }
    }
}
</script>

<style scoped>
.wallet-checkout-section {
    padding: 0 8px 16px 8px;
}
</style>
